import React, { useState, useEffect } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import { useAuth } from '../contexts/AuthContext';
import AddPlayerModal from './AddPlayerModal';
import ProfileEditModal from './ProfileEditModal';
import api, { getApiImageUrl } from '../services/api';

const ProfileScreen = ({ currentPlayer, onPlayerSelect, loadSavedPlayer }) => {
  const { t } = useLocalization();
  const { user, logout, updateUser, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('activity');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [isFirstPlayer, setIsFirstPlayer] = useState(false);
  const [contactForm, setContactForm] = useState({ subject: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [playerStats, setPlayerStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(true);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/api/dashboard');
      setDashboardData(response.data);
      
      // Check if this is the first time user logs in (no players)
      if (response.data.players && response.data.players.length === 0) {
        setIsFirstPlayer(true);
        setShowAddPlayerModal(true);
      } else if (response.data.players && response.data.players.length > 0) {
        // Try to load saved player first, fallback to first player if no saved player or not found
        if (!currentPlayer && onPlayerSelect) {
          const savedPlayerLoaded = loadSavedPlayer ? loadSavedPlayer(response.data.players) : false;
          if (!savedPlayerLoaded) {
            // No saved player found, use first player as default
            onPlayerSelect(response.data.players[0]);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchDashboardData();
  }, []);

  // Fetch statistics for all players when dashboard data changes
  useEffect(() => {
    const fetchAllPlayerStats = async () => {
      if (!dashboardData?.players) {
        setLoadingStats(false);
        return;
      }
      
      setLoadingStats(true);
      const statsMap = {};
      
      for (const player of dashboardData.players) {
        const stats = await fetchPlayerStatistics(player.id);
        if (stats) {
          statsMap[player.id] = stats;
        }
      }
      
      setPlayerStats(statsMap);
      setLoadingStats(false);
    };

    fetchAllPlayerStats();
  }, [dashboardData?.players]);

  const handleLogout = () => {
    logout();
  };

  const handleAddPlayer = () => {
    setIsFirstPlayer(false);
    setShowAddPlayerModal(true);
  };

  const handlePlayerAdded = (newPlayer) => {
    // Update dashboard data with new player
    setDashboardData(prev => ({
      ...prev,
      players: [...(prev.players || []), newPlayer]
    }));
    
    // Set the new player as current player
    if (onPlayerSelect) {
      onPlayerSelect(newPlayer);
    }
    
    // Check if this was the first player (tour should start)
    if (isFirstPlayer && !newPlayer.initial_tour_completed) {
      // Navigate to play screen to start the tour
      setTimeout(() => {
        window.location.href = '/app/play';
      }, 1500);
    }
    
    setIsFirstPlayer(false);
  };

  const handleCloseModal = () => {
    setShowAddPlayerModal(false);
    setIsFirstPlayer(false);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    
    try {
      // This would send email to app maintainer
      const response = await api.post('/api/contact', {
        subject: contactForm.subject,
        message: contactForm.message,
        userEmail: user?.email,
        userName: `${user?.first_name} ${user?.last_name}`
      });
      
      setContactMessage(response.data.message || t('profile.contactSuccess'));
      setContactForm({ subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      const errorMessage = error.response?.data?.error || t('profile.contactError');
      setContactMessage(errorMessage);
    }
    
    setContactLoading(false);
    setTimeout(() => setContactMessage(''), 5000);
  };

  const fetchProfileData = async () => {
    try {
      const response = await api.get('/api/profile');
      setProfileData(response.data.user);
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    }
  };

  const handleEditProfile = async () => {
    // Fetch current profile data before opening modal
    await fetchProfileData();
    setShowProfileEditModal(true);
  };

  const handleProfileUpdate = async (updatedUser) => {
    // Update profile data
    setProfileData(updatedUser);
    
    // Update user in AuthContext (this will update it everywhere)
    updateUser(updatedUser);
    
    // Also refresh user data from backend to ensure consistency
    await refreshUser();
    
    // Force re-render by refreshing dashboard data
    fetchDashboardData();
  };

  // Fetch player statistics from backend
  const fetchPlayerStatistics = async (playerId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await api.get(`/api/player/${playerId}/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch player statistics:', error);
      return null;
    }
  };

  const getActivityLevel = (activity) => {
    if (activity === 0) return 'none';
    if (activity <= 3) return 'low';
    if (activity <= 7) return 'medium';
    if (activity <= 12) return 'high';
    return 'very-high';
  };

  // Tabs configuration - moved after loading check to ensure dashboardData is available
  const tabs = [
    { 
      key: 'activity', 
      label: t('profile.activity'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      count: dashboardData?.players?.length || 0
    },
    { 
      key: 'account', 
      label: t('profile.account'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    { 
      key: 'contact', 
      label: t('profile.contact'), 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
  ];


  const renderActivityTab = () => {
    if (loadingStats) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
          <span className="ml-2 text-gray-600">{t('profile.loadingStats')}</span>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {dashboardData?.players && dashboardData.players.length > 0 ? (
          dashboardData.players.map(player => {
            const currentYear = new Date().getFullYear();
            const age = currentYear - player.birth_year;
            const stats = playerStats[player.id];
            
            if (!stats) {
              return (
                <div key={player.id} className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="text-center text-gray-500">
                    Failed to load statistics for {player.first_name}
                  </div>
                </div>
              );
            }

          const getAvatarColor = (filename) => {
            if (!filename) return '#667eea';
            let hash = 0;
            for (let i = 0; i < filename.length; i++) {
              const char = filename.charCodeAt(i);
              hash = ((hash << 5) - hash) + char;
              hash = hash & hash;
            }
            const hue = Math.abs(hash) % 360;
            return `hsl(${hue}, 85%, 75%)`;
          };

          return (
            <div key={player.id} className="bg-white rounded-2xl shadow-lg p-6 space-y-6">
              {/* Player Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: getAvatarColor(player.avatar || 'default') }}
                  >
                    {player.avatar && player.avatar.endsWith('.png') ? (
                      <img 
                        src={getApiImageUrl(`/api/avatars/${player.avatar}`)} 
                        alt={player.first_name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl">{player.avatar || '👦'}</span>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">{player.first_name} {player.last_name}</h3>
                    <p className="text-gray-600">{t('profile.ageYears', { age })}</p>
                    {currentPlayer?.id === player.id && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('profile.currentlyActive')}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Switch Player Button */}
                {currentPlayer?.id !== player.id && (
                  <button
                    onClick={() => onPlayerSelect && onPlayerSelect(player)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span>{t('profile.makeActive')}</span>
                  </button>
                )}
              </div>

              {/* Stats Section */}
              <div className="space-y-6">
                {/* Stats Header */}
                <div className="text-center">
                  <h4 className="text-xl font-semibold text-gray-800 mb-2">{t('profile.solvedTasks')}</h4>
                  <p className="text-sm text-gray-600">{t('profile.solvedTasksDescription')}</p>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
                  <div className="bg-blue-50 rounded-xl p-3 md:p-4 text-center">
                    <div className="text-lg md:text-2xl font-bold text-blue-600">{stats.task_stats.tasks_last_day}</div>
                    <div className="text-xs text-gray-500">{t('profile.tasksLabel')}</div>
                    <div className="text-xs md:text-sm font-medium text-gray-700">{t('profile.lastDay')}</div>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3 md:p-4 text-center">
                    <div className="text-lg md:text-2xl font-bold text-purple-600">{stats.task_stats.tasks_last_week}</div>
                    <div className="text-xs text-gray-500">{t('profile.tasksLabel')}</div>
                    <div className="text-xs md:text-sm font-medium text-gray-700">{t('profile.lastWeek')}</div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 md:p-4 text-center">
                    <div className="text-lg md:text-2xl font-bold text-green-600">{stats.task_stats.tasks_last_month}</div>
                    <div className="text-xs text-gray-500">{t('profile.tasksLabel')}</div>
                    <div className="text-xs md:text-sm font-medium text-gray-700">{t('profile.lastMonth')}</div>
                  </div>
                  <div className="bg-yellow-50 rounded-xl p-3 md:p-4 text-center">
                    <div className="text-lg md:text-2xl font-bold text-yellow-600">{stats.task_stats.tasks_all_time}</div>
                    <div className="text-xs text-gray-500">{t('profile.tasksLabel')}</div>
                    <div className="text-xs md:text-sm font-medium text-gray-700">{t('profile.allTime')}</div>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3 md:p-4 text-center col-span-2 md:col-span-1">
                    <div className="text-lg md:text-2xl font-bold text-red-600">{stats.module_stats.completed_modules}</div>
                    <div className="text-xs text-gray-500">{t('profile.modulesLabel')}</div>
                    <div className="text-xs md:text-sm font-medium text-gray-700">{t('profile.completed')}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
          <div className="text-6xl mb-4">👥</div>
          <p className="text-gray-600 mb-6">{t('dashboard.noPlayers')}</p>
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            onClick={handleAddPlayer}
          >
            + {t('dashboard.addPlayer')}
          </button>
        </div>
      )}
    </div>
    );
  };

  const renderAccountTab = () => (
    <div className="space-y-8">
      {/* User Info Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800">{t('dashboard.userInfo')}</h3>
          <button 
            onClick={handleEditProfile}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            {t('profile.editProfile')}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-500">{t('auth.firstName')}</label>
            <div className="text-lg font-semibold text-gray-800">{user?.first_name}</div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-500">{t('auth.lastName')}</label>
            <div className="text-lg font-semibold text-gray-800">{user?.last_name}</div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-500">{t('auth.email')}</label>
            <div className="text-lg font-semibold text-gray-800">{user?.email}</div>
          </div>
        </div>
      </div>

      {/* Players Management */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800">{t('dashboard.players')}</h3>
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            onClick={handleAddPlayer}
          >
            <span>+</span>
            <span>{t('dashboard.addPlayer')}</span>
          </button>
        </div>
        
        {dashboardData?.players && dashboardData.players.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dashboardData.players.map(player => {
              const currentYear = new Date().getFullYear();
              const age = currentYear - player.birth_year;
              const getAvatarColor = (filename) => {
                if (!filename) return '#667eea';
                let hash = 0;
                for (let i = 0; i < filename.length; i++) {
                  const char = filename.charCodeAt(i);
                  hash = ((hash << 5) - hash) + char;
                  hash = hash & hash;
                }
                const hue = Math.abs(hash) % 360;
                return `hsl(${hue}, 85%, 75%)`;
              };
              
              return (
                <div 
                  key={player.id} 
                  className={`p-4 rounded-xl border-2 transition-all cursor-pointer hover:shadow-lg ${
                    currentPlayer?.id === player.id 
                      ? 'border-blue-500 bg-blue-50 shadow-lg' 
                      : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                  }`}
                  onClick={() => onPlayerSelect && onPlayerSelect(player)}
                >
                  <div className="flex items-center space-x-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center shadow-md"
                      style={{ backgroundColor: getAvatarColor(player.avatar || 'default') }}
                    >
                      {player.avatar && player.avatar.endsWith('.png') ? (
                        <img 
                          src={getApiImageUrl(`/api/avatars/${player.avatar}`)} 
                          alt={player.first_name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-xl">{player.avatar || '👦'}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-semibold text-gray-800 truncate">
                        {player.first_name} {player.last_name}
                      </h4>
                      <p className="text-sm text-gray-600">{t('profile.ageYears', { age })} ({player.birth_year})</p>
                      <p className="text-sm text-gray-600">
                        {player.sex === 'M' ? t('addPlayer.male') : t('addPlayer.female')}
                      </p>
                      {player.school_name && (
                        <p className="text-sm text-gray-600 truncate">{player.school_name}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👥</div>
            <p className="text-gray-600">{t('dashboard.noPlayers')}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderContactTab = () => (
    <div className="bg-white rounded-2xl shadow-lg p-6 max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="text-4xl mb-4">📬</div>
        <h3 className="text-2xl font-semibold text-gray-800 mb-2">{t('profile.contactForm')}</h3>
        <p className="text-gray-600">{t('profile.contactDescription')}</p>
      </div>
      
      <form onSubmit={handleContactSubmit} className="space-y-6">
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
            {t('profile.subject')}
          </label>
          <input
            type="text"
            id="subject"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
            value={contactForm.subject}
            onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
            required
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            {t('profile.message')}
          </label>
          <textarea
            id="message"
            rows={6}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors resize-none"
            value={contactForm.message}
            onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
            required
            placeholder={t('profile.contactPlaceholder')}
          />
        </div>

        {contactMessage && (
          <div className="p-4 rounded-lg bg-green-50 border border-green-200">
            <p className="text-green-800">{contactMessage}</p>
          </div>
        )}

        <button 
          type="submit" 
          className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={contactLoading}
        >
          {contactLoading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              <span>{t('profile.sending')}</span>
            </div>
          ) : (
            t('profile.sendMessage')
          )}
        </button>
      </form>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const getTabStyles = (tab) => {
    const isActive = activeTab === tab.key;
    return `inline-flex items-center space-x-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${
      isActive 
        ? 'border-blue-500 text-blue-600' 
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 space-y-3 md:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-900">{t('navigation.profile')}</h1>
            </div>
            
            <div className="flex items-center justify-between md:justify-end space-x-4">
              {user && (
                <div className="flex items-center space-x-3">
                  <div className="text-right hidden sm:block">
                    <div className="text-sm font-medium text-gray-900">{user.first_name} {user.last_name}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </div>
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </span>
                  </div>
                </div>
              )}
              
              <button 
                className="flex items-center space-x-2 px-3 md:px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                onClick={handleLogout}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="hidden sm:inline">{t('dashboard.logout')}</span>
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <nav className="flex space-x-4 md:space-x-8 border-b border-gray-200 -mb-px overflow-x-auto">
            {!loading && tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={getTabStyles(tab)}
              >
                {tab.icon}
                <span className="whitespace-nowrap">{tab.label}</span>
                {tab.count !== undefined && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    activeTab === tab.key 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Content Area */}
      <main className="max-w-7xl mx-auto p-4 md:p-6 overflow-hidden">
        <div className="w-full">
          {activeTab === 'activity' && renderActivityTab()}
          {activeTab === 'account' && renderAccountTab()}
          {activeTab === 'contact' && renderContactTab()}
        </div>
      </main>

      {/* Add Player Modal */}
      <AddPlayerModal
        isOpen={showAddPlayerModal}
        onClose={handleCloseModal}
        onPlayerAdded={handlePlayerAdded}
        isFirstPlayer={isFirstPlayer}
      />

      {/* Profile Edit Modal */}
      <ProfileEditModal
        isOpen={showProfileEditModal}
        onClose={() => setShowProfileEditModal(false)}
        user={profileData || user}
        onUserUpdate={handleProfileUpdate}
      />
    </div>
  );
};

export default ProfileScreen;